import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { z } from 'zod';
import { addResourceNameToQueryResult, useLegacyAxiosInstance } from './utils';
import type { ApiError } from './utils/types';

const TemplatesSchema = z.array(
  z
    .object({
      id: z.string(),
      attributes: z.object({
        name: z.string(),
        step_type: z.string(),
        plan_type: z.string(),
        include_pdf: z.boolean(),
        include_ubl: z.boolean(),
        updated_at: z.string(),
      }),
    })
    .transform(item => ({
      id: item.id,
      ...item.attributes,
    })),
);

export type Templates = z.infer<typeof TemplatesSchema>;

export async function loadTemplatesFn(
  axiosInstance: Promise<AxiosInstance>,
  stepType?: 'invoice' | 'credit_note' | 'additional_reminder',
) {
  const instance = await axiosInstance;
  const { data } = await instance.get('/templates', { params: { step_type: stepType } });

  return TemplatesSchema.parse(data.data);
}

export function useLoadTemplates(stepType?: 'invoice' | 'credit_note') {
  const axiosInstance = useLegacyAxiosInstance();

  const queryResult = useQuery<Templates, ApiError>({
    queryKey: ['templates'],
    queryFn: () => loadTemplatesFn(axiosInstance, stepType),
  });

  return addResourceNameToQueryResult<Templates, ApiError, 'templates'>('templates', queryResult);
}
